/**
 * PROPARV Real Estate Calculation Utilities - Frontend
 */

export function calculateMAO(arv, rehabCost, strategy = 'flip') {
  const multipliers = {
    flip: 0.70,
    hold: 0.75,
    brrrr: 0.75,
    wholesale: 0.65,
    airbnb: 0.72
  };
  const multiplier = multipliers[strategy] || 0.70;
  return Math.max(0, (arv * multiplier) - rehabCost);
}

export function calculateROI(profit, totalInvestment) {
  if (!totalInvestment || totalInvestment <= 0) return 0;
  return Number(((profit / totalInvestment) * 100).toFixed(2));
}

export function calculateCapRate(noi, propertyValue) {
  if (!propertyValue || propertyValue <= 0) return 0;
  return Number(((noi / propertyValue) * 100).toFixed(2));
}

export function calculateCashFlow(monthlyRent, expenses = {}) {
  const {
    mortgage = 0,
    taxes = 0,
    insurance = 0,
    hoa = 0,
    maintenance = 0,
    vacancy = 0,
    propertyManagement = 0,
    utilities = 0
  } = expenses;

  const vacancyRate = vacancy || (monthlyRent * 0.05);
  const maintenanceRate = maintenance || (monthlyRent * 0.05);
  const totalExpenses = mortgage + taxes + insurance + hoa + maintenanceRate + vacancyRate + propertyManagement + utilities;
  return Number((monthlyRent - totalExpenses).toFixed(2));
}

export function calculateDealScore(arv, purchasePrice, rehabCost, monthlyRent, location = 'B') {
  let score = 0;

  const allInCost = purchasePrice + rehabCost;
  const equityRatio = (arv - allInCost) / arv;

  if (equityRatio >= 0.30) score += 40;
  else if (equityRatio >= 0.20) score += 30;
  else if (equityRatio >= 0.10) score += 20;
  else if (equityRatio >= 0.05) score += 10;
  else if (equityRatio < 0) score += 0;
  else score += 5;

  const rentToPrice = monthlyRent / purchasePrice;
  if (rentToPrice >= 0.015) score += 30;
  else if (rentToPrice >= 0.01) score += 22;
  else if (rentToPrice >= 0.007) score += 14;
  else if (rentToPrice >= 0.005) score += 7;
  else score += 0;

  const locationScores = { 'A': 20, 'B': 15, 'C': 8, 'D': 2 };
  score += locationScores[location] || 15;

  const rehabToArv = rehabCost / arv;
  if (rehabToArv <= 0.10) score += 10;
  else if (rehabToArv <= 0.20) score += 7;
  else if (rehabToArv <= 0.30) score += 4;
  else score += 0;

  return Math.min(100, Math.max(0, Math.round(score)));
}

export function calculateMortgage(principal, rate, termYears) {
  if (!principal || principal <= 0 || !rate || rate <= 0 || !termYears || termYears <= 0) {
    return { monthly: 0, totalInterest: 0, totalPayment: 0 };
  }

  const monthlyRate = rate / 100 / 12;
  const numPayments = termYears * 12;

  const monthly = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
    (Math.pow(1 + monthlyRate, numPayments) - 1);

  const totalPayment = monthly * numPayments;
  const totalInterest = totalPayment - principal;

  return {
    monthly: Number(monthly.toFixed(2)),
    totalInterest: Number(totalInterest.toFixed(2)),
    totalPayment: Number(totalPayment.toFixed(2))
  };
}

export function calculateHoldingCosts(months, taxes = 0, insurance = 0, utilities = 0, hoa = 0, other = 0) {
  const monthly = taxes + insurance + utilities + hoa + other;
  const total = monthly * months;

  return {
    total: Number(total.toFixed(2)),
    monthly: Number(monthly.toFixed(2)),
    months,
    breakdown: {
      taxes: Number((taxes * months).toFixed(2)),
      insurance: Number((insurance * months).toFixed(2)),
      utilities: Number((utilities * months).toFixed(2)),
      hoa: Number((hoa * months).toFixed(2)),
      other: Number((other * months).toFixed(2))
    }
  };
}

export function calculateBRRRR(arv, rehabCost, purchasePrice, afterRefiValue) {
  const totalInvested = purchasePrice + rehabCost;
  const refinanceAmount = afterRefiValue || (arv * 0.75);
  const cashLeft = Math.max(0, totalInvested - refinanceAmount);
  const equityCreated = arv - totalInvested;
  const cashRecovered = Math.min(refinanceAmount, totalInvested);
  const cashRecoveryPercent = totalInvested > 0 ? (cashRecovered / totalInvested) * 100 : 0;

  return {
    totalInvested: Number(totalInvested.toFixed(2)),
    refinanceAmount: Number(refinanceAmount.toFixed(2)),
    cashLeft: Number(cashLeft.toFixed(2)),
    equityCreated: Number(equityCreated.toFixed(2)),
    cashRecovered: Number(cashRecovered.toFixed(2)),
    cashRecoveryPercent: Number(cashRecoveryPercent.toFixed(2)),
    infiniteReturn: cashLeft <= 0
  };
}

export function formatCurrency(value) {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  return `${Number(value).toFixed(decimals)}%`;
}

export function getDealLabel(score) {
  if (score >= 70) return 'Great Deal';
  if (score >= 40) return 'Average Deal';
  return 'Bad Deal';
}

export function getDealColor(score) {
  if (score >= 70) return '#10b981';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
}
